import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  DateRange, DictionaryEntryModel, DictionaryKey,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumber,
  extractNumberArr,
  HxCurrencyPipe,
  HxJoinedPipe,
  HxSpendService,
  HxUiLabelPipe,
  isoDate,
  SpendingFullModel,
  toRouterQueryParams
} from 'hx-services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbModal, NgbModalRef, NgbPagination, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { HxDateRangeSelectComponent, HxDateSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { SpendCategorySearchComponent } from '../spend-category-search/spend-category-search.component';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'app-spend-list',
    templateUrl: './spend-list.component.html',
    styleUrls: ['./spend-list.component.css'],
    imports: [RouterLink, HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, SpendCategorySearchComponent, HxDateSelectComponent, NgIf, NgFor, NgClass, NgbTooltip, HxLoaderComponent, NgbPagination, DatePipe, TranslocoPipe, HxCurrencyPipe, HxUiLabelPipe, HxJoinedPipe]
})
export class SpendListComponent implements OnInit, OnDestroy {
  @ViewChild('templateRef') public templateRef: TemplateRef<any>;
  list: SpendingFullModel[] = [];
  isLoading = {
    list: false,
  };
  pagination = {allItemCount: 0};
  params: {
    storeIds?: number[];
    page?: number;
    limit?: number;
    date?: DateRange;
    payBefore?: DateRange;
    incomingNumber?: string;
    paidDate?: string;
    spendCategoryCode?: string;
    cashflowCode?: string;
  } = {page: 1, limit: 20};
  lang: string;
  dialogRef?: NgbModalRef;
  private sub: Subscription;

  constructor(
    private spendService: HxSpendService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private tr: TranslocoService,
  ) {
  }

  ngOnInit() {
    this.lang = this.tr.getActiveLang();
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      this.params.storeIds = extractNumberArr('storeIds', paramMap);
      if (this.params.storeIds.length === 0) {
        this.params.storeIds = extractDefaultStoreIds();
      }
      this.params.spendCategoryCode = paramMap.get('spendCategoryCode');
      this.params.cashflowCode = paramMap.get('cashflowCode');
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      this.params.date = extractDateRange('date', paramMap);
      this.params.payBefore = extractDateRange('payBefore', paramMap);
      this.params.paidDate = paramMap.get('paidDate');
      this.params.incomingNumber = paramMap.get('incomingNumber');
      this.loadList();
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  resetFilter() {
    this.pagination.allItemCount = 0;
    this.params = {page: 1, limit: 20};
    this.params.date = undefined;
    this.params.payBefore = undefined;
    this.navigateUrl();
  }

  loadList() {
    if (this.params.storeIds) {
      this.isLoading.list = true;
      this.spendService.getSpendStoreList(this.params).then(result => {
        this.list = result.list;
        this.pagination.allItemCount = result.count;
        this.isLoading.list = false;
      }, () => {
        this.isLoading.list = false;
      });
    } else {
      this.list = [];
    }
  }

  pageChanged(page: number) {
    this.params.page = page;
    this.navigateUrl();
  }

  applyFilters() {
    this.navigateUrl();
  }

  spendCategoryChanged(spendCategory: DictionaryEntryModel) {
    this.params.spendCategoryCode = spendCategory.code;
    this.navigateUrl();
  }

  cashflowCategoryChanged(cashflowCategory: DictionaryEntryModel) {
    this.params.cashflowCode = cashflowCategory.code;
    this.navigateUrl();
  }

  showDeleteDialog(item: SpendingFullModel) {
    this.dialogRef = this.modalService.open(this.templateRef, {size: 'sm', centered: true});
    this.dialogRef.result.then(() => {
      this.spendService.remove(item.id).then(() => {
        this.toastr.success(this.tr.translate('spend-list.ts.deleteSuccess'));
        this.loadList();
      });
    }, () => {
    });
  }

  private navigateUrl() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  protected readonly DictionaryKey = DictionaryKey;
}
